import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = 'Africa Respiratory Care Association (ARCA)',
  description = 'ARCA empowers Africa\'s respiratory care professionals through education, training, research, and advocacy. Join us in creating sustainable solutions for better breathing, better care, and better lives across Africa.',
  keywords = 'respiratory care, Africa healthcare, respiratory therapy, medical training, respiratory education, ARCA, breathing health, pulmonary care, ventilation, oxygen therapy, critical care, healthcare professionals Africa',
  author = 'Africa Respiratory Care Association',
  image = '/logo512.png',
  url,
  type = 'website',
  twitterCard = 'summary_large_image',
  structuredData,
  canonicalUrl
}) => {
  // Generate full URL
  const siteUrl = 'https://www.africaresp.org';
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const fullImageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  const canonical = canonicalUrl || fullUrl;

  // Default structured data for organization
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'Africa Respiratory Care Association',
    'alternateName': 'ARCA',
    'url': siteUrl,
    'logo': `${siteUrl}/logo512.png`,
    'description': description,
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'Customer Service',
      'email': 'info@africaresp.org'
    },
    'sameAs': [
      'https://www.facebook.com/africaresp',
      'https://twitter.com/africaresp',
      'https://www.linkedin.com/company/africaresp',
      'https://www.youtube.com/@africaresp'
    ],
    'address': {
      '@type': 'PostalAddress',
      'addressCountry': 'Africa'
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="Africa Respiratory Care Association" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:creator" content="@africaresp" />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
